import React from "react";
import Image from "next/image";
import { useLatestServiceData } from "@/contexts/HomeDataContext";
import { ServiceItem } from "@/types/homePage";
import { FadeIn, Stagger } from "@/components/ui/scroll-animated";
import { useTranslations } from 'next-intl';

export const LatestServiceSection: React.FC = () => {
  const data = useLatestServiceData();
  const t = useTranslations('LatestService');

  return (
    <section className="relative w-full py-16 md:py-20 lg:py-24 bg-white overflow-visible">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <FadeIn delay={200} className="mb-4 md:mb-6">
            <div className="bg-brand-gradient-text font-h1-up font-[number:var(--h1-up-font-weight)] text-[length:var(--h1-up-font-size)] tracking-[var(--h1-up-letter-spacing)] leading-[var(--h1-up-line-height)] [font-style:var(--h1-up-font-style)]">
              {t('tagline')}
            </div>
          </FadeIn>
          
          <FadeIn delay={300}>
            <h2 className="font-heading-h1 font-[number:var(--heading-h1-font-weight)] text-x-900 text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-[length:var(--heading-h1-font-size)] tracking-[var(--heading-h1-letter-spacing)] leading-tight md:leading-tight lg:leading-[var(--heading-h1-line-height)] [font-style:var(--heading-h1-font-style)]">
              {t('heading')}
            </h2>
          </FadeIn>
        </div>

        {/* Service Cards Grid */}
        <div className="flex flex-wrap justify-center gap-6 md:gap-8 -mb-48 md:-mb-48 lg:-mb-56 relative z-20">
          {data.services.map((service: ServiceItem, index: number) => (
            <Stagger
              key={service.id}
              delay={400}
              staggerDelay={200}
              index={index}
              className="group"
            >
              <div className="relative bg-white rounded-lg border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 w-[300px] h-[383px] overflow-hidden shadow-lg">
                
                {/* Icon Circle Background */}
                <div className="absolute top-8 left-1/2 transform -translate-x-1/2 w-[132px] h-[132px] rounded-full bg-brand-gradient flex items-center justify-center">
                  
                  {/* Service Icon */}
                  <div className="w-16 h-16 flex items-center justify-center">
                    <Image
                      src={service.icon}
                      alt={`${service.title} icon`}
                      width={64}
                      height={64}
                      className="w-16 h-16 object-contain filter brightness-0 invert"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="absolute top-48 left-0 right-0 px-6 pb-6 md:px-8 md:pb-8 text-center">
                  <h3 className="font-heading-h3 font-[number:var(--heading-h3-font-weight)] text-x-900 text-lg md:text-xl lg:text-2xl tracking-[var(--heading-h3-letter-spacing)] leading-[var(--heading-h3-line-height)] [font-style:var(--heading-h3-font-style)] mb-4 md:mb-6">
                    {t(`services.${service.id}.title`)}
                  </h3>
                  <p className="font-body-m font-[number:var(--body-m-font-weight)] text-gray-600 text-sm md:text-base tracking-[var(--body-m-letter-spacing)] leading-[var(--body-m-line-height)] [font-style:var(--body-m-font-style)]">
                    {t(`services.${service.id}.description`)}
                  </p>
                </div>
              </div>
            </Stagger>
          ))}
        </div>
      </div>
    </section>
  );
};

